import type { MouseEvent } from "react";
import { Box, ButtonBase, Link, Typography, useMediaQuery } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { principlePresentation } from "@chessla/shared";
import { useMainScrollElement } from "@/app/context/MainScrollContext";
import { FormatIcon, MotifBadge, PrincipleGlyph, ReviewEvidenceGlyph } from "@/shared/components";
import { layoutTokens, marketingLiftSx } from "@/shared/theme";
import {
  HELP_GLOSSARY_GROUPS,
  HELP_PRINCIPLES,
  HELP_PRINCIPLE_SOURCE_BY_ID,
  HELP_PRINCIPLE_SOURCES,
  HELP_RELATIONSHIPS,
  HELP_SCREENSHOTS,
  HELP_SECTIONS,
} from "./help-content";
import { HelpProductFigure } from "./HelpProductFigure";
import { POSITION_RELATIONSHIP_LEGEND, RelationshipGlyph } from "@/features/board";

export { HELP_GLOSSARY_GROUPS, HELP_PRINCIPLE_SOURCE_BY_ID, HELP_SCREENSHOTS, HELP_SECTIONS } from "./help-content";

const Figure = ({ section }: { section: string }) => {
  const shot = HELP_SCREENSHOTS.find((item) => item.section === section);
  return shot ? <HelpProductFigure shot={shot} compact /> : null;
};

type GlossaryVisualData = (typeof HELP_GLOSSARY_GROUPS)[number]["entries"][number]["visual"];

const GlossaryVisual = ({ visual }: { visual: GlossaryVisualData }) => {
  const frame = {
    width: 40,
    height: 40,
    display: "grid",
    placeItems: "center",
    border: "2px solid",
    fontSize: ".72rem",
    fontWeight: 950,
    lineHeight: 1,
  } as const;
  if (visual.kind === "motif")
    return (
      <Box data-glossary-visual="motif" sx={{ width: 40, height: 40 }}>
        <MotifBadge kind={visual.motif} label={visual.mark} color={visual.color} />
      </Box>
    );
  if (visual.kind === "principle")
    return (
      <Box data-glossary-visual="principle" sx={{ ...frame, color: "secondary.main", borderColor: "secondary.main" }}>
        <PrincipleGlyph principle={visual.principle} />
      </Box>
    );
  if (visual.kind === "review")
    return (
      <Box data-glossary-visual="review" sx={{ ...frame, color: "secondary.main", borderColor: "secondary.main" }}>
        <ReviewEvidenceGlyph kind={visual.review} />
      </Box>
    );
  if (visual.kind === "format")
    return (
      <Box
        data-glossary-visual="format"
        sx={{
          ...frame,
          color: "secondary.main",
          borderColor: "secondary.main",
          overflow: "hidden",
          "& svg, & .MuiSvgIcon-root": { width: 26, height: 26, maxWidth: 26, fontSize: 26 },
          "& p": { fontSize: 24, lineHeight: 1 },
        }}
      >
        <FormatIcon format={visual.format} />
      </Box>
    );
  return (
    <Box
      data-glossary-visual="notation"
      sx={{
        ...frame,
        color: "secondary.main",
        borderColor: "secondary.main",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: visual.mark.length > 3 ? ".62rem" : ".72rem",
      }}
    >
      {visual.mark}
    </Box>
  );
};

export const HelpPage = () => {
  const mainScroll = useMainScrollElement();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const navigateSection = (id: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    const behavior = reducedMotion ? "auto" : "smooth";
    if (mainScroll) {
      const top =
        mainScroll.scrollTop + target.getBoundingClientRect().top - mainScroll.getBoundingClientRect().top - 24;
      mainScroll.scrollTo({ top, behavior });
    } else target.scrollIntoView({ behavior, block: "start" });
    window.history.replaceState(window.history.state, "", `#${id}`);
  };

  return (
    <Box
      component="main"
      sx={{
        width: "100%",
        maxWidth: layoutTokens.workspaceMaxWidth,
        minHeight: layoutTokens.screenMinHeight,
        mx: "auto",
        px: layoutTokens.workspacePagePadding,
        py: { xs: 5, md: 8 },
        overflowX: "clip",
      }}
    >
      <Box
        component="header"
        data-tour="help-intro"
        sx={{ pb: { xs: 5, md: 8 }, borderBottom: "2px solid", borderColor: "primary.main" }}
      >
        <Typography sx={{ color: "secondary.main", fontSize: ".68rem", fontWeight: 950, letterSpacing: ".14em" }}>
          CHESSLA / OPERATING MANUAL
        </Typography>
        <Typography
          component="h1"
          sx={{
            mt: 2,
            maxWidth: "10ch",
            fontSize: { xs: "3.5rem", sm: "5.5rem", lg: "7.5rem" },
            fontWeight: 950,
            letterSpacing: "-.085em",
            lineHeight: 0.8,
          }}
        >
          HOW CHESSLA WORKS
        </Typography>
        <Typography
          sx={{
            mt: 3,
            maxWidth: 760,
            color: "text.secondary",
            fontSize: { xs: ".9rem", md: "1.05rem" },
            lineHeight: 1.7,
          }}
        >
          Nikola&apos;s field guide to turning your finished games into decisions you can inspect, test, and remember.
          Every fact keeps its source.
        </Typography>
        <Link
          href="mailto:support@chessla.net?subject=Chessla%20bug%20report"
          sx={{ display: "inline-block", mt: 2, fontWeight: 700 }}
        >
          Report a bug
        </Link>
      </Box>
      <Box
        sx={{
          display: { md: "grid" },
          gridTemplateColumns: "210px minmax(0,1fr)",
          gap: { md: 7 },
          alignItems: "start",
        }}
      >
        <Box
          component="nav"
          aria-label="Help sections"
          data-tour="help-sections"
          sx={{
            position: { md: "sticky" },
            top: { md: 24 },
            display: "flex",
            flexDirection: { xs: "row", md: "column" },
            flexWrap: "wrap",
            gap: 0.5,
            py: { xs: 3, md: 5 },
            maxHeight: { md: "calc(100dvh - 48px)" },
            overflowY: { md: "auto" },
          }}
        >
          {HELP_SECTIONS.map(({ id }) => (
            <Link
              key={id}
              href={`#${id}`}
              onClick={navigateSection(id)}
              underline="none"
              sx={{
                minHeight: 36,
                display: "flex",
                alignItems: "center",
                px: 1,
                color: "text.secondary",
                borderLeft: { md: "2px solid transparent" },
                fontSize: ".64rem",
                fontWeight: 900,
                letterSpacing: ".055em",
                textTransform: "uppercase",
                "&:hover,&:focus-visible": {
                  color: "background.default",
                  bgcolor: "primary.main",
                  borderColor: "secondary.main",
                },
              }}
            >
              {id === "start" ? "Start" : id}
            </Link>
          ))}
        </Box>
        <Box>
          {HELP_SECTIONS.map((section) => {
            const shotIndex = HELP_SCREENSHOTS.findIndex(({ section: id }) => id === section.id);
            const hasFigure = shotIndex >= 0;
            return (
              <Box
                key={section.id}
                id={section.id}
                component="section"
                data-tour="help-article"
                aria-labelledby={`${section.id}-title`}
                sx={{ py: { xs: 5, md: 8 }, scrollMarginTop: 24, borderBottom: "1px solid", borderColor: "divider" }}
              >
                <Box
                  data-help-layout={hasFigure ? "editorial-split" : "editorial-copy"}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "minmax(0,1fr)",
                      lg: hasFigure ? "minmax(260px,.78fr) minmax(420px,1.22fr)" : "minmax(0,1fr)",
                    },
                    gap: { xs: 3, lg: 5 },
                    alignItems: "start",
                  }}
                >
                  <Box sx={{ minWidth: 0, order: { lg: hasFigure && shotIndex % 2 === 1 ? 2 : 1 } }}>
                    <Typography
                      sx={{
                        color: "secondary.main",
                        fontSize: ".66rem",
                        fontWeight: 950,
                        letterSpacing: ".13em",
                        textTransform: "uppercase",
                      }}
                    >
                      {section.kicker}
                    </Typography>
                    <Typography
                      id={`${section.id}-title`}
                      component="h2"
                      sx={{
                        mt: 1.5,
                        fontSize: { xs: "2.2rem", md: hasFigure ? "clamp(2.5rem,4vw,3.4rem)" : "3.6rem" },
                        fontWeight: 950,
                        letterSpacing: "-.065em",
                        lineHeight: 0.92,
                      }}
                    >
                      {section.title}
                    </Typography>
                    <Box sx={{ mt: 2.5, display: "grid", gap: 1.3 }}>
                      {section.body.map((paragraph) => (
                        <Typography
                          key={paragraph}
                          sx={{
                            maxWidth: 820,
                            color: "text.secondary",
                            fontSize: { xs: ".82rem", md: ".92rem" },
                            lineHeight: 1.75,
                          }}
                        >
                          {paragraph}
                        </Typography>
                      ))}
                    </Box>
                    {section.id === "principles" && (
                      <>
                        <Box
                          sx={{
                            mt: 4,
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", lg: "repeat(2,minmax(0,1fr))" },
                            borderTop: "1px solid",
                            borderLeft: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          {HELP_PRINCIPLES.map(({ id, phase, basis }) => {
                            const copy = principlePresentation(id);
                            return (
                              <Box
                                id={`principle-${id.replaceAll("_", "-")}`}
                                key={id}
                                component="article"
                                sx={{
                                  minWidth: 0,
                                  p: { xs: 1.5, md: 2 },
                                  borderRight: "1px solid",
                                  borderBottom: "1px solid",
                                  borderColor: "divider",
                                  scrollMarginTop: 24,
                                }}
                              >
                                <Box
                                  data-principle-heading="true"
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "32px minmax(0,1fr)",
                                    gap: 1,
                                    alignItems: "start",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      display: "grid",
                                      placeItems: "center",
                                      color: "secondary.main",
                                      border: "2px solid currentColor",
                                    }}
                                  >
                                    <PrincipleGlyph principle={id} />
                                  </Box>
                                  <Box sx={{ minWidth: 0, display: "grid", gap: 0 }}>
                                    <Typography
                                      component="h3"
                                      sx={{
                                        fontSize: ".86rem",
                                        fontWeight: 950,
                                        letterSpacing: ".035em",
                                        lineHeight: 1.05,
                                      }}
                                    >
                                      {copy.name}
                                    </Typography>
                                    <Link
                                      data-principle-source="true"
                                      href={HELP_PRINCIPLE_SOURCE_BY_ID[id]}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      underline="none"
                                      aria-label={`Learn about ${copy.traditionalName} on Chess.com`}
                                      sx={{
                                        minHeight: { xs: 28, md: 18 },
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.4,
                                        color: "text.secondary",
                                        fontSize: ".72rem",
                                        fontWeight: 800,
                                        lineHeight: 1.05,
                                        "& .principle-source-arrow": {
                                          color: "secondary.main",
                                          opacity: 0,
                                          transform: "translate(-4px,4px)",
                                          transition: "opacity 180ms ease, transform 240ms cubic-bezier(.22,1,.36,1)",
                                        },
                                        "&:hover,&:focus-visible": { color: "primary.main" },
                                        "&:hover .principle-source-arrow,&:focus-visible .principle-source-arrow": {
                                          opacity: 1,
                                          transform: "translate(0,0)",
                                        },
                                        "@media (prefers-reduced-motion: reduce)": {
                                          "& .principle-source-arrow": { transition: "none", transform: "none" },
                                        },
                                      }}
                                    >
                                      {copy.traditionalName}
                                      <Box component="span" className="principle-source-arrow" aria-hidden>
                                        ↗
                                      </Box>
                                    </Link>
                                  </Box>
                                </Box>
                                <Typography
                                  sx={{
                                    mt: 0.25,
                                    color: "secondary.main",
                                    fontSize: ".58rem",
                                    fontWeight: 950,
                                    letterSpacing: ".1em",
                                    lineHeight: 1,
                                  }}
                                >
                                  {phase.toUpperCase()}
                                </Typography>
                                <Typography sx={{ mt: 0.4, fontSize: ".73rem", lineHeight: 1.45 }}>{basis}</Typography>
                                <Typography
                                  sx={{ mt: 0.4, color: "text.secondary", fontSize: ".7rem", lineHeight: 1.45 }}
                                >
                                  {copy.cue}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                        <Box sx={{ mt: 2.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {HELP_PRINCIPLE_SOURCES.map((source) => (
                            <Link
                              key={source.href}
                              href={source.href}
                              target="_blank"
                              rel="noreferrer"
                              sx={{
                                minHeight: 36,
                                display: "inline-flex",
                                alignItems: "center",
                                px: 1,
                                border: "1px solid",
                                borderColor: "divider",
                                color: "text.secondary",
                                fontSize: ".58rem",
                                fontWeight: 900,
                                letterSpacing: ".06em",
                                "&:hover,&:focus-visible": { borderColor: "secondary.main", color: "primary.main" },
                              }}
                            >
                              {source.label} ↗
                            </Link>
                          ))}
                        </Box>
                      </>
                    )}
                    {section.id === "glossary" && (
                      <Box component="section" aria-label="Board relationships" sx={{ mt: 4 }}>
                        <Typography
                          component="h3"
                          sx={{
                            mb: 1.25,
                            color: "secondary.main",
                            fontSize: ".64rem",
                            fontWeight: 950,
                            letterSpacing: ".12em",
                            textTransform: "uppercase",
                          }}
                        >
                          Board relationships
                        </Typography>
                        <Typography sx={{ mb: 2, color: "text.secondary", fontSize: ".75rem", lineHeight: 1.6 }}>
                          These badges and arrow colors describe the displayed position. Select a piece for support,
                          x-rays, and discovered-attack potential, or enable All verified relationships. Relationships
                          are recalculated when the position changes; alignments alone do not prove a winning tactic.
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", lg: "repeat(2,minmax(0,1fr))" },
                            borderTop: "1px solid",
                            borderLeft: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          {(Object.keys(HELP_RELATIONSHIPS) as (keyof typeof HELP_RELATIONSHIPS)[]).map((kind) => {
                            const { label } = POSITION_RELATIONSHIP_LEGEND[kind];
                            const { description, source } = HELP_RELATIONSHIPS[kind];
                            return (
                              <Box
                                component="article"
                                id={`relationship-${kind}`}
                                key={kind}
                                aria-labelledby={`relationship-${kind}-heading`}
                                sx={{
                                  minWidth: 0,
                                  p: { xs: 1.5, md: 2 },
                                  borderRight: "1px solid",
                                  borderBottom: "1px solid",
                                  borderColor: "divider",
                                  scrollMarginTop: 24,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "32px minmax(0,1fr)",
                                    gap: 1,
                                    alignItems: "start",
                                  }}
                                >
                                  <Box aria-hidden sx={{ width: 32, height: 32 }}>
                                    <RelationshipGlyph kind={kind} />
                                  </Box>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      component="h4"
                                      id={`relationship-${kind}-heading`}
                                      sx={{
                                        fontSize: ".86rem",
                                        fontWeight: 950,
                                        letterSpacing: ".035em",
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {label}
                                    </Typography>
                                    <Link
                                      data-relationship-source="true"
                                      href={source}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      aria-label={`Learn about ${label} on Chess.com`}
                                      underline="hover"
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        minHeight: 28,
                                        color: "text.secondary",
                                        fontSize: ".72rem",
                                        fontWeight: 800,
                                        "&:hover,&:focus-visible": { color: "primary.main" },
                                      }}
                                    >
                                      Chess.com{" "}
                                      <Box component="span" aria-hidden sx={{ ml: 0.4 }}>
                                        ↗
                                      </Box>
                                    </Link>
                                  </Box>
                                </Box>
                                <Typography
                                  sx={{ mt: 1, color: "text.secondary", fontSize: ".75rem", lineHeight: 1.6 }}
                                >
                                  {description}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                    {section.id === "glossary" && (
                      <Box sx={{ mt: 4, display: "grid", gap: 4 }}>
                        {HELP_GLOSSARY_GROUPS.map((group) => (
                          <Box key={group.id}>
                            <Typography
                              component="h3"
                              sx={{
                                mb: 1.25,
                                color: "secondary.main",
                                fontSize: ".64rem",
                                fontWeight: 950,
                                letterSpacing: ".12em",
                                textTransform: "uppercase",
                              }}
                            >
                              {group.title}
                            </Typography>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "minmax(0,1fr)", lg: "repeat(2,minmax(0,1fr))" },
                                borderTop: "1px solid",
                                borderLeft: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              {group.entries.map((entry) => (
                                <Box
                                  key={entry.id}
                                  component="article"
                                  sx={{
                                    minWidth: 0,
                                    p: 2,
                                    display: "grid",
                                    gridTemplateColumns: "40px minmax(0,1fr)",
                                    gap: 1.5,
                                    borderRight: "1px solid",
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                  }}
                                >
                                  <GlossaryVisual visual={entry.visual} />
                                  <Box>
                                    <Typography
                                      component="h4"
                                      sx={{ fontSize: ".8rem", fontWeight: 950, letterSpacing: ".025em" }}
                                    >
                                      {entry.label}
                                    </Typography>
                                    <Typography
                                      sx={{ mt: 0.55, color: "text.secondary", fontSize: ".7rem", lineHeight: 1.55 }}
                                    >
                                      {entry.definition}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {section.id === "profile" && (
                      <Typography sx={{ mt: 3, fontSize: ".72rem", fontWeight: 900 }}>
                        <Link
                          href={`${import.meta.env.VITE_MARKETING_URL ?? "https://chessla.net"}/privacy`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          PRIVACY
                        </Link>{" "}
                        ·{" "}
                        <Link
                          href={`${import.meta.env.VITE_MARKETING_URL ?? "https://chessla.net"}/terms`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          TERMS
                        </Link>
                      </Typography>
                    )}
                  </Box>
                  {hasFigure && (
                    <Box sx={{ minWidth: 0, order: { lg: shotIndex % 2 === 1 ? 1 : 2 }, pt: { lg: 1 } }}>
                      <Figure section={section.id} />
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      <ButtonBase
        component={RouterLink}
        to="/chat?new=1"
        sx={{
          my: 5,
          width: "100%",
          minHeight: { xs: 132, md: 180 },
          p: { xs: 2.5, md: 4 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 3,
          textAlign: "left",
          bgcolor: "primary.main",
          color: "background.default",
          border: "2px solid",
          borderColor: "primary.main",
          ...marketingLiftSx(),
          "&:hover, &:focus-visible": { ...marketingLiftSx()["&:hover, &:focus-visible"], bgcolor: "primary.main" },
        }}
      >
        <Box>
          <Typography sx={{ fontSize: ".64rem", fontWeight: 950, letterSpacing: ".14em" }}>
            NEED FURTHER HELP?
          </Typography>
          <Typography
            sx={{
              mt: 1,
              display: "flex",
              alignItems: "baseline",
              gap: { xs: 0.6, md: 1 },
              fontSize: { xs: "2rem", md: "3.4rem" },
              fontWeight: 950,
              letterSpacing: "-.06em",
              lineHeight: 0.9,
            }}
          >
            ASK NIKOLA
            <Box
              component="span"
              aria-hidden
              sx={{ display: "inline-block", fontSize: ".9em", lineHeight: 0.72, transform: "translateY(.03em)" }}
            >
              ♞
            </Box>
          </Typography>
        </Box>
        <Typography aria-hidden sx={{ fontSize: { xs: "2rem", md: "3rem" }, lineHeight: 1 }}>
          ↗
        </Typography>
      </ButtonBase>
    </Box>
  );
};
